import { type ComponentProps, type ReactNode, useMemo } from 'react';
import moment from 'moment-timezone';
import { Box, HStack, Pressable, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';

import { Thermometer02Icon } from '../icons/thermometer-02';
import { AlarmLevel as AlarmLevelEnum, InstanceDetailsDto, LinkedInstancesDto } from '@pimm/services/lib/store-equipment';
import { Text } from '@pimm/base';
import { tempValue } from '../_helper';
import { useEquipmentTranslate } from '../hooks/equipment-translate.hook';
import { DoorEventIcon } from '../icons/door-event';
import { EventBox } from './event-box';

export const TempColors = {
  [AlarmLevelEnum.Info]: 'moss',
  [AlarmLevelEnum.Warning]: 'warning',
  [AlarmLevelEnum.Alarm]: 'error',
  [AlarmLevelEnum.Dead]: 'gray',
  [AlarmLevelEnum.Ok]: 'blue',
} as const;

type SensorState = 'reporting' | 'offline' | 'disabled';

const PACIFIC_TZ = 'America/Los_Angeles';

const formatLastUpdateDate = (lastUpdateDate?: string | null, utcOffsetInMinutes?: number): string => {
  if (!lastUpdateDate) return '';
  return moment
    .tz(lastUpdateDate, PACIFIC_TZ)
    .utcOffset(utcOffsetInMinutes ?? -420)
    .format('MM/DD/YYYY h:mm A');
};

const getSensorState = (instance?: InstanceDetailsDto): SensorState => {
  if (instance?.OutOfService) return 'disabled';
  if (!instance?.IsOnline) return 'offline';
  return 'reporting';
};

type SensorRowProps = {
  icon: ReactNode;
  state: SensorState;
  lastUpdateDate?: string | null;
  utcOffsetInMinutes?: number;
  eventBoxes: ReactNode;
  valueText: string;
  valueSize?: string;
  valueColor: string;
};

const SensorRow = ({ icon, state, lastUpdateDate, utcOffsetInMinutes, eventBoxes, valueText, valueSize = '2xl', valueColor }: SensorRowProps) => {
  const { t } = useTranslation();

  const formattedLastUpdate = useMemo(() => formatLastUpdateDate(lastUpdateDate, utcOffsetInMinutes), [lastUpdateDate, utcOffsetInMinutes]);

  return (
    <HStack alignItems="center" w="full">
      <Box size={6} alignItems="center" justifyContent="center">
        {icon}
      </Box>
      {state === 'reporting' ? (
        <>
          <HStack flex={1} h={9} space={1} alignItems="center" px={1}>
            {eventBoxes}
          </HStack>
          <Text size={valueSize as any} fontWeight={700} color={valueColor}>
            {valueText}
          </Text>
        </>
      ) : (
        <>
          <VStack space={0} justifyContent="center" h={9} flex={1} px={1} opacity={0.5}>
            <Text size="xs" fontWeight={600} color={valueColor}>
              {t('store_equipment:last_update')}:
            </Text>
            <Text size="sm" fontWeight={600} color={valueColor}>
              {formattedLastUpdate}
            </Text>
          </VStack>
          <Text size="md" fontWeight={700} color={valueColor} opacity={0.5}>
            {state === 'disabled' ? t('store_equipment:disabled') : t('store_equipment:offline')}
          </Text>
        </>
      )}
    </HStack>
  );
};

export type InstanceTemperatureCardProps = {
  _container?: ComponentProps<typeof VStack>;
  instance: LinkedInstancesDto;
  doorInstance?: LinkedInstancesDto;
  utcOffsetInMinutes?: number;
  isFocused?: boolean;
  onPress?: () => void;
};

export const InstanceTemperatureCard = ({ instance, doorInstance, utcOffsetInMinutes, isFocused, ...props }: InstanceTemperatureCardProps) => {
  const { t } = useTranslation();
  const { et } = useEquipmentTranslate();

  const tempSensor = instance.Temperature;
  const doorSensor = doorInstance?.Door;

  const tempState = getSensorState(tempSensor);
  const doorState = doorInstance ? getSensorState(doorSensor) : undefined;

  const isNotReporting = tempState !== 'reporting';
  const color = isNotReporting ? 'gray' : TempColors[tempSensor?.AlarmLevel ?? AlarmLevelEnum.Dead];

  const doorValueText = useMemo(() => {
    const isOpen = ['open', '2', '2.00'].includes(doorSensor?.StaticValue?.toLowerCase() ?? '');
    return isOpen ? t('store_equipment:open') : t('store_equipment:closed');
  }, [doorSensor, t]);

  const titleColor = isNotReporting ? 'black' : `${color}.900`;
  const tempIconColor = isNotReporting ? `${color}.800` : `${color}.900`;
  const dividerColor = isNotReporting ? 'gray.300' : `${color}.300`;

  const opacity = (state?: SensorState) => {
    if (state === 'reporting') return 1;
    return 0.6;
  };

  return (
    <Pressable
      rounded="xl"
      w="full"
      borderWidth={2}
      borderColor={isFocused ? 'primary.600' : `${color}.200`}
      bgColor={`${color}.200`}
      onPress={props.onPress}
    >
      <VStack py={2} px={3} w="full" space={1} {...props._container}>
        <Text size="md" fontWeight={700} lineHeight="xs" color={titleColor} numberOfLines={2}>
          {et(tempSensor?.Description)}
        </Text>

        <VStack space={1}>
          <SensorRow
            icon={<Thermometer02Icon size="sm" color={tempIconColor} opacity={opacity(tempState)} />}
            state={tempState ?? 'offline'}
            lastUpdateDate={tempSensor?.LastUpdateDate}
            utcOffsetInMinutes={utcOffsetInMinutes}
            eventBoxes={
              <>
                <EventBox instance={instance.Temperature} eventType="Temperature" />
                <EventBox instance={instance.BatteryVoltage} eventType="Battery" />
              </>
            }
            valueText={tempValue(tempSensor?.StaticValue)}
            valueColor={`${color}.900`}
          />

          {!!doorInstance && (
            <>
              <Box h={0} borderBottomWidth={1} borderColor={dividerColor} />
              <SensorRow
                icon={<DoorEventIcon size="sm" color={`${color}.900`} opacity={opacity(doorState)} />}
                state={doorState ?? 'offline'}
                lastUpdateDate={doorSensor?.LastUpdateDate}
                utcOffsetInMinutes={utcOffsetInMinutes}
                eventBoxes={
                  <>
                    <EventBox instance={doorInstance.Door} eventType="Door" />
                    <EventBox instance={doorInstance.BatteryVoltage} eventType="Battery" />
                  </>
                }
                valueText={doorValueText}
                valueSize="md"
                valueColor={`${color}.900`}
              />
            </>
          )}
        </VStack>
      </VStack>
    </Pressable>
  );
};
